import { useQuery } from "@tanstack/vue-query";
import {
  computed,
  reactive,
  type ComputedRef,
  type MaybeRefOrGetter,
  toValue,
} from "vue";

import api from "@/services/api";
import { usePublicDashboardQueryFilters } from "@/services/public_dashboard";

// Types
export interface FeatureFlagStatus {
  enabled: boolean;
  consentPending: boolean;
}

export type FeatureFlags = Record<string, FeatureFlagStatus>;

// Query keys
export const featureFlagsKeys = {
  all: ["feature-flags"] as const,
  flags: (params: { dashboardId?: number; token?: string } = {}) =>
    [...featureFlagsKeys.all, "flags", params] as const,
};

// Fetch function
async function fetchFeatureFlags(
  dashboardId?: number,
  token?: string,
): Promise<FeatureFlags> {
  const params: Record<string, string> = {};
  if (dashboardId && token) {
    params.dashboard = String(dashboardId);
    params.token = token;
  }

  const response = await api.get<FeatureFlags>("/feature-flags/flags/", {
    params,
  });
  return response.data;
}

// Main composable
export function useFeatureFlags(options?: {
  dashboardId?: MaybeRefOrGetter<number | undefined>;
  token?: MaybeRefOrGetter<string | undefined>;
  enabled?: MaybeRefOrGetter<boolean>;
}) {
  function dashboardId() {
    return toValue(options?.dashboardId);
  }
  function token() {
    return toValue(options?.token);
  }
  function enabled() {
    return toValue(options?.enabled) ?? true;
  }

  return reactive(
    useQuery({
      queryKey: computed(() =>
        featureFlagsKeys.flags({
          dashboardId: dashboardId(),
          token: token(),
        }),
      ),
      queryFn: () => fetchFeatureFlags(dashboardId(), token()),
      enabled,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }),
  );
}

// Convenience composable for public dashboards
export function usePublicDashboardFeatureFlags() {
  const { dashboard, token } = usePublicDashboardQueryFilters();

  return useFeatureFlags({
    dashboardId: dashboard,
    token,
  });
}

// Helper to check a specific flag - returns true only when fully usable
// (flag enabled AND consent satisfied). Follows useIsManager/useIsStaff pattern from user.ts.
export function useIsFeatureFlagEnabled(
  flagKey: string,
  options?: {
    dashboardId?: MaybeRefOrGetter<number | undefined>;
    token?: MaybeRefOrGetter<string | undefined>;
  },
): ComputedRef<boolean> {
  const flags = useFeatureFlags(options);
  return computed(() => {
    const flag = flags.data?.[flagKey];
    return flag?.enabled === true && !flag?.consentPending;
  });
}

// Helper returning the 3-state status of a feature flag:
// - isVisible: flag is enabled (regardless of consent) — use for showing/hiding UI elements
// - isUsable: flag is enabled AND consent is satisfied — use for enabling interactions
// - consentPending: flag is enabled but consent has not been given
// - isLoading: the underlying flags query has not yet resolved. Callers
//   that gate other fetches on `isVisible` need this to avoid treating
//   the initial `false` like a real opt-out.
// - isError: the underlying flags query failed. Without this, callers
//   can't distinguish a network error from a confirmed opt-out — both
//   collapse to `isUsable === false` once the query resolves — and a
//   transient flag-fetch failure silently disables the gated feature.
export function useFeatureFlagStatus(
  flagKey: string,
  options?: {
    dashboardId?: MaybeRefOrGetter<number | undefined>;
    token?: MaybeRefOrGetter<string | undefined>;
  },
): {
  isVisible: ComputedRef<boolean>;
  isUsable: ComputedRef<boolean>;
  consentPending: ComputedRef<boolean>;
  isLoading: ComputedRef<boolean>;
  isError: ComputedRef<boolean>;
  refetch: () => void;
} {
  const flags = useFeatureFlags(options);

  const isVisible = computed(() => flags.data?.[flagKey]?.enabled === true);

  const consentPending = computed(
    () =>
      flags.data?.[flagKey]?.enabled === true &&
      flags.data?.[flagKey]?.consentPending === true,
  );

  const isUsable = computed(() => isVisible.value && !consentPending.value);

  // Mirror the query's native loading state so disabled / idle queries
  // (where no fetch is in flight) don't keep `isLoading` stuck at true.
  const isLoading = computed(() => flags.isLoading);
  const isError = computed(() => flags.isError === true);

  // Expose the underlying query refetch so a "retry" affordance can clear
  // a flag-load failure too, not just the downstream AI queries.
  function refetch(): void {
    flags.refetch();
  }

  return { isVisible, isUsable, consentPending, isLoading, isError, refetch };
}
